const LOCAL_ERASMUS_FALLBACK_LOGO = "/assets/img/erasmuskonkursi/adriatiklogo.png";
const LOCAL_NEWS_FALLBACK_IMAGE = "/assets/img/logo/fpepblog.png";

const normalizeText = (value = "") => String(value || "").trim();

const slugify = (value = "") =>
  normalizeText(value)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/đ/gi, "dj")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)+/g, "");

const toArray = (value) => {
  if (Array.isArray(value)) return value;
  return value ? [value] : [];
};

export const uniqueCandidates = (...candidateLists) => {
  const seen = new Set();

  return candidateLists
    .flatMap((list) => toArray(list))
    .map((candidate) => normalizeText(candidate))
    .filter(Boolean)
    .filter((candidate) => {
      if (seen.has(candidate)) return false;
      seen.add(candidate);
      return true;
    });
};

const normalizeCandidateGroup = (value) => uniqueCandidates(value);

const mergeCandidateGroups = (primaryGroups = [], fallbackGroups = []) => {
  const maxLength = Math.max(primaryGroups.length, fallbackGroups.length);
  const merged = [];

  for (let index = 0; index < maxLength; index += 1) {
    const group = uniqueCandidates(primaryGroups[index] || [], fallbackGroups[index] || []);
    if (group.length) merged.push(group);
  }

  return merged;
};

const getLocalNewsSlug = (post) => post?.attributes?.Slug || slugify(post?.attributes?.Title || "");

const findLocalNewsPost = (cmsPost, localPosts = []) => {
  if (!cmsPost || !Array.isArray(localPosts) || !localPosts.length) return null;

  const ids = uniqueCandidates(cmsPost.id, cmsPost.legacy?.id, cmsPost.cms?.legacy?.id);
  const slug = cmsPost.attributes?.Slug || cmsPost.slug || cmsPost.cms?.slug || "";

  return localPosts.find((localPost) => {
    const localId = normalizeText(localPost?.id);
    const localSlug = getLocalNewsSlug(localPost);

    return (
      ids.includes(localId) ||
      Boolean(slug && localSlug === slug) ||
      Boolean(slug && `${localId}-${localSlug}` === slug)
    );
  }) || null;
};

const getPostGalleryGroups = (post) => {
  const galleryItems = post?.attributes?.Gallery?.data || [];
  return toArray(galleryItems).map(normalizeCandidateGroup).filter((group) => group.length);
};

const getLocalPostGalleryGroups = (localPost) => {
  const galleryItems = localPost?.attributes?.Gallery?.data || [];
  return toArray(galleryItems).map((item) => uniqueCandidates(item)).filter((group) => group.length);
};

export const mergeNewsPostWithLocalMediaFallback = (cmsPost, localPosts = []) => {
  if (!cmsPost) return cmsPost;

  const localPost = findLocalNewsPost(cmsPost, localPosts);
  if (!localPost) return cmsPost;

  const cmsImageCandidates = uniqueCandidates(
    cmsPost.attributes?.ImageCandidates || [],
    cmsPost.attributes?.Image,
  );
  const localImage = localPost?.attributes?.Image;
  const imageCandidates = uniqueCandidates(cmsImageCandidates, localImage, LOCAL_NEWS_FALLBACK_IMAGE);

  const mergedGalleryGroups = mergeCandidateGroups(
    getPostGalleryGroups(cmsPost),
    getLocalPostGalleryGroups(localPost),
  );

  return {
    ...cmsPost,
    attributes: {
      ...cmsPost.attributes,
      Image: imageCandidates[0] || cmsPost.attributes?.Image || localImage || LOCAL_NEWS_FALLBACK_IMAGE,
      ImageCandidates: imageCandidates,
      Gallery: {
        ...(cmsPost.attributes?.Gallery || {}),
        data: mergedGalleryGroups,
      },
    },
  };
};

export const mergeNewsPostsWithLocalMediaFallbacks = (cmsPosts = [], localPosts = []) => {
  return cmsPosts.map((post) => mergeNewsPostWithLocalMediaFallback(post, localPosts));
};

const findLocalGalleryAlbum = (cmsAlbum, localAlbums = []) => {
  if (!cmsAlbum || !Array.isArray(localAlbums) || !localAlbums.length) return null;

  const ids = uniqueCandidates(cmsAlbum.id, cmsAlbum.legacy?.id, cmsAlbum.cms?.legacy?.id);
  const titleSlug = slugify(cmsAlbum.title || cmsAlbum.description || "");

  return localAlbums.find((localAlbum) => {
    const localId = normalizeText(localAlbum?.id);
    const localTitleSlug = slugify(localAlbum?.description || "");

    return ids.includes(localId) || Boolean(titleSlug && localTitleSlug === titleSlug);
  }) || null;
};

const getAlbumCandidateGroups = (album) => {
  const coverGroup = uniqueCandidates(album?.imgCandidates || [], album?.img);
  const detailGroups = album?.subImageCandidates?.length
    ? album.subImageCandidates.map(normalizeCandidateGroup)
    : toArray(album?.subImages).map(normalizeCandidateGroup);

  return [coverGroup, ...detailGroups].filter((group) => group.length);
};

const getLocalAlbumCandidateGroups = (localAlbum) => {
  const coverGroup = uniqueCandidates(localAlbum?.img);
  const detailGroups = toArray(localAlbum?.subImages).map((item) => uniqueCandidates(item));
  return [coverGroup, ...detailGroups].filter((group) => group.length);
};

export const mergeGalleryAlbumWithLocalMediaFallback = (cmsAlbum, localAlbums = []) => {
  if (!cmsAlbum) return cmsAlbum;

  const localAlbum = findLocalGalleryAlbum(cmsAlbum, localAlbums);
  if (!localAlbum) return cmsAlbum;

  const mergedGroups = mergeCandidateGroups(
    getAlbumCandidateGroups(cmsAlbum),
    getLocalAlbumCandidateGroups(localAlbum),
  );

  if (!mergedGroups.length) return cmsAlbum;

  return {
    ...cmsAlbum,
    img: mergedGroups[0]?.[0] || cmsAlbum.img || localAlbum.img || "",
    imgCandidates: mergedGroups[0] || [],
    subImages: mergedGroups.slice(1).map((group) => group[0]).filter(Boolean),
    subImageCandidates: mergedGroups.slice(1),
    totalPhotos: Math.max(
      Number(cmsAlbum.totalPhotos || 0),
      mergedGroups.length,
      1,
    ),
  };
};

export const mergeGalleryAlbumsWithLocalMediaFallbacks = (cmsAlbums = [], localAlbums = []) => {
  return cmsAlbums.map((album) => mergeGalleryAlbumWithLocalMediaFallback(album, localAlbums));
};

const findLocalErasmusCall = (cmsCall, localCalls = []) => {
  if (!cmsCall || !Array.isArray(localCalls) || !localCalls.length) return null;

  const ids = uniqueCandidates(cmsCall.id, cmsCall.legacy?.id, cmsCall.cms?.legacy?.id, cmsCall.slug);

  return localCalls.find((localCall) => {
    const localIds = uniqueCandidates(localCall?.id, localCall?.slug);
    return localIds.some((localId) => ids.includes(localId));
  }) || null;
};

export const mergeErasmusCallWithLocalLogoFallback = (cmsCall, localCalls = []) => {
  if (!cmsCall) return cmsCall;

  const localCall = findLocalErasmusCall(cmsCall, localCalls);
  const existingCandidates = uniqueCandidates(cmsCall.logoCandidates || [], cmsCall.logo);
  const nonGenericExistingCandidates = existingCandidates.filter(
    (candidate) => candidate !== LOCAL_ERASMUS_FALLBACK_LOGO,
  );
  const logoCandidates = uniqueCandidates(
    nonGenericExistingCandidates,
    localCall?.logo,
    LOCAL_ERASMUS_FALLBACK_LOGO,
  );

  return {
    ...cmsCall,
    logo: logoCandidates[0] || cmsCall.logo || localCall?.logo || LOCAL_ERASMUS_FALLBACK_LOGO,
    logoCandidates,
  };
};

export const mergeErasmusCallsWithLocalLogoFallbacks = (cmsCalls = [], localCalls = []) => {
  return cmsCalls.map((call) => mergeErasmusCallWithLocalLogoFallback(call, localCalls));
};
